import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
  Link,
  Chip,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  CreditCard as CreditCardIcon,
  CheckCircle as CheckIcon,
  ArrowBack as ArrowBackIcon,
  Check as CheckMarkIcon,
  Close as CloseIcon,
  PhoneAndroid as AndroidIcon,
  CloudDone as CloudIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  SupportAgent as SupportIcon,
  IntegrationInstructions as IntegrationIcon
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

import subscriptionService from '../../services/subscriptionService';
import tenantService from '../../services/tenantService';
import { storeUserData } from '../../services/authService';
import { formatCurrency } from '../../utils/currencyFormatter';
import axiosInstance from '../../utils/axios';
import API_CONFIG from '../../config/api.config';

// Professional Color Palette (Matching Login)
const COLORS = {
  primary: '#0d47a1', // Deep corporate blue
  secondary: '#1976d2', // Brighter blue
  accent: '#1565c0',
  background: '#f8fafd',
  cardBg: 'rgba(255, 255, 255, 0.95)',
  text: '#0a192f',
  muted: '#455a64',
  success: '#2e7d32',
  border: 'rgba(0,0,0,0.08)'
};

const FONT_FAMILY = '"Inter", "Roboto", "Helvetica Neue", "Arial", sans-serif';

const formatPrice = (price, currencyCode = 'USD') => {
  return formatCurrency(price, { currencyCode });
};

const PLAN_FEATURE_FLAGS = [
  { id: 'loyalty_points', label: 'loyalty points' },
  { id: 'whatsapp_bill_share', label: 'whatsapp bill share' },
  { id: 'ecommerce_website', label: 'ecommerce website' },
  { id: 'webapp', label: 'web app' },
  { id: 'android_app', label: 'android app' },
];

const steps = [
  { label: 'Company Info', icon: <BusinessIcon /> },
  { label: 'Admin Account', icon: <PersonIcon /> },
  { label: 'Select Plan', icon: <CreditCardIcon /> },
  { label: 'Complete', icon: <CheckIcon /> }
];

/**
 * Sidebar Content for Registration (Steps 0 & 1)
 */
const RegistrationSidebar = ({ step }) => {
  const sidebarContent = [
    {
      title: "Transform Your Business",
      description: "Join 500+ successful businesses using Tiya POS. Our cloud-based ERP solution is designed to scale with your growth.",
      features: [
        { text: "GST Compliant Billing", icon: <CloudIcon color="primary" /> },
        { text: "Real-time Inventory Sync", icon: <SpeedIcon color="primary" /> },
        { text: "Enterprise-Grade Security", icon: <SecurityIcon color="primary" /> }
      ],
      lottie: `${process.env.PUBLIC_URL || ''}/lotties/registraion1.lottie`
    },
    {
      title: "Secure Admin Access",
      description: "Setup your primary administrator account. We implement bank-level security protocols to keep your business data safe.",
      features: [
        { text: "Role-Based Permissions", icon: <SecurityIcon color="primary" /> },
        { text: "Multi-Factor Authentication", icon: <IntegrationIcon color="primary" /> },
        { text: "24/7 Dedicated Support", icon: <SupportIcon color="primary" /> }
      ],
      lottie: `${process.env.PUBLIC_URL || ''}/lotties/registraion2.lottie`
    }
  ];

  const current = sidebarContent[step] || sidebarContent[0];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', pr: { md: 4 } }}>
      <Fade in={true} key={step} timeout={800}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: COLORS.text, mb: 2, lineHeight: 1.2 }}>
            {current.title}
          </Typography>
          <Typography variant="body1" sx={{ color: COLORS.muted, mb: 4, fontSize: '1.1rem', lineHeight: 1.6 }}>
            {current.description}
          </Typography>

          <List sx={{ mb: 4 }}>
            {current.features.map((f, i) => (
              <ListItem key={i} sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {f.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={f.text} 
                  primaryTypographyProps={{ fontWeight: 600, color: COLORS.text }} 
                />
              </ListItem>
            ))}
          </List>

          <Box sx={{ width: '100%', height: 250, borderRadius: 4, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.5)', border: '1px solid rgba(0,0,0,0.05)' }}>
            <DotLottieReact
              src={current.lottie}
              autoplay
              loop
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
        </Box>
      </Fade>
    </Box>
  );
};

/**
 * Step 1: Company Information
 */
const CompanyInfoStep = ({ data, onChange, onNext, loading, whatsappOtpEnabled }) => {
  const [errors, setErrors] = useState({});
  const [gstStates, setGstStates] = useState([]);
  const [otpSent, setOtpSent] = useState(false);
  const [otpSessionId, setOtpSessionId] = useState(null);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [verificationToken, setVerificationToken] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(API_CONFIG.ENDPOINTS.STATES);
        if (!cancelled && Array.isArray(res.data)) {
          setGstStates(res.data);
        }
      } catch (e) {
        console.error('Failed to load GST states', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (!whatsappOtpEnabled) return;
    if (otpValue.length === 6 && otpSessionId && !verifyingOtp && !otpVerified) {
      handleVerifyOtp();
    }
  }, [otpValue]);

  const validateForm = () => {
    const newErrors = {};
    if (!data.companyName?.trim()) newErrors.companyName = 'Company name is required';
    if (!data.addressLine1?.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    if (!data.addressLine2?.trim()) newErrors.addressLine2 = 'Address Line 2 is required';
    if (!data.stateCode?.trim()) newErrors.stateCode = 'State (GST) is required';
    if (!data.companyPhone?.trim()) newErrors.companyPhone = 'Mobile is required';
    else if (!/^\d{10,15}$/.test(data.companyPhone.trim())) newErrors.companyPhone = 'Mobile must be 10-15 digits';
    if (whatsappOtpEnabled && !otpVerified) newErrors.otpVerification = 'Please verify your mobile number';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      if (whatsappOtpEnabled) {
        onChange('mobileVerificationToken', verificationToken);
      } else {
        onChange('mobileVerificationToken', null);
      }
      onNext();
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 15);
    onChange('companyPhone', value);
    if (!whatsappOtpEnabled) return;
    if (otpSent || otpVerified) {
      setOtpSent(false);
      setOtpSessionId(null);
      setOtpVerified(false);
      setOtpValue('');
      setOtpError('');
      setVerificationToken(null);
      setCountdown(0);
    }
  };

  const handleSendOtp = async () => {
    const phone = data.companyPhone?.trim();
    if (!phone || !/^\d{10,15}$/.test(phone)) {
      setErrors(prev => ({ ...prev, companyPhone: 'Please enter a valid 10-15 digit mobile number' }));
      return;
    }
    setSendingOtp(true);
    setOtpError('');
    try {
      const response = await tenantService.sendRegistrationOtp(phone);
      if (response.success) {
        setOtpSent(true);
        setOtpSessionId(response.sessionId);
        setCountdown(response.cooldownSeconds || 30);
        toast.success('OTP sent to your WhatsApp!');
      } else {
        setOtpError(response.message || 'Failed to send OTP');
        if (response.cooldownSeconds > 0) setCountdown(response.cooldownSeconds);
        toast.error(response.message || 'Failed to send OTP');
      }
    } catch (error) {
      setOtpError(error.message || 'Failed to send OTP');
      toast.error(error.message || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpValue || otpValue.length !== 6) {
      setOtpError('Please enter 6-digit OTP');
      return;
    }
    setVerifyingOtp(true);
    setOtpError('');
    try {
      const response = await tenantService.verifyRegistrationOtp(data.companyPhone, otpValue, otpSessionId);
      if (response.verified) {
        setOtpVerified(true);
        setVerificationToken(response.verificationToken);
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.otpVerification;
          return newErrors;
        });
        toast.success('Mobile number verified successfully!');
      } else {
        setOtpError(response.message || 'Invalid OTP');
      }
    } catch (error) {
      setOtpError(error.message || 'Failed to verify OTP');
    } finally {
      setVerifyingOtp(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: COLORS.primary }}>Company Profile</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Company Name"
            value={data.companyName || ''}
            onChange={(e) => onChange('companyName', e.target.value)}
            error={!!errors.companyName}
            helperText={errors.companyName}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 1"
            value={data.addressLine1 || ''}
            onChange={(e) => onChange('addressLine1', e.target.value)}
            error={!!errors.addressLine1}
            helperText={errors.addressLine1}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Address Line 2"
            value={data.addressLine2 || ''}
            onChange={(e) => onChange('addressLine2', e.target.value)}
            error={!!errors.addressLine2}
            helperText={errors.addressLine2}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.stateCode} required>
            <InputLabel>State (GST)</InputLabel>
            <Select
              label="State (GST)"
              value={data.stateCode ?? ''}
              onChange={(e) => onChange('stateCode', e.target.value)}
            >
              {gstStates.map((s) => (
                <MenuItem key={s.stateCode} value={s.stateCode}>
                  {s.name} ({s.stateCode})
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{errors.stateCode}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box>
            <Box display="flex" gap={1}>
              <TextField
                fullWidth
                label="Mobile"
                value={data.companyPhone || ''}
                onChange={handlePhoneChange}
                error={!!errors.companyPhone}
                helperText={
                  errors.companyPhone ||
                  (whatsappOtpEnabled ? 'WhatsApp number (OTP will be sent here)' : 'Contact mobile number')
                }
                required
                disabled={whatsappOtpEnabled && otpVerified}
                InputProps={{
                  endAdornment: whatsappOtpEnabled && otpVerified ? <CheckIcon sx={{ color: COLORS.success }} /> : null
                }}
              />
              {whatsappOtpEnabled && !otpSent && !otpVerified && (
                <Button variant="contained" onClick={handleSendOtp} disabled={sendingOtp || !data.companyPhone || data.companyPhone.length < 10} sx={{ height: 56 }}>
                  {sendingOtp ? <CircularProgress size={20} color="inherit" /> : 'Verify'}
                </Button>
              )}
            </Box>
            {whatsappOtpEnabled && otpSent && !otpVerified && (
              <Box mt={2} p={2} sx={{ bgcolor: '#f5f9ff', borderRadius: 2, border: `1px solid ${COLORS.primary}` }}>
                <TextField
                  fullWidth
                  label="Enter OTP"
                  value={otpValue}
                  onChange={(e) => setOtpValue(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  error={!!otpError}
                  helperText={otpError}
                  autoFocus
                />
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography variant="caption">{countdown > 0 ? `Resend in ${countdown}s` : <Link component="button" onClick={handleSendOtp}>Resend OTP</Link>}</Typography>
                  <Button onClick={handleVerifyOtp} disabled={verifyingOtp || otpValue.length !== 6}>Submit</Button>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Tax Number (Optional)"
            value={data.taxNumber || ''}
            onChange={(e) => onChange('taxNumber', e.target.value)}
          />
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="flex-end" mt={4}>
        <Button
          variant="contained"
          size="large"
          onClick={handleNext}
          disabled={loading || (whatsappOtpEnabled && !otpVerified)}
          sx={{ px: 6 }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Step 2: Admin Account
 */
const AdminAccountStep = ({ data, onChange, onNext, onBack, loading }) => {
  const [errors, setErrors] = useState({});
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);

  const checkEmail = async (email) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return true;
    setEmailChecking(true);
    try {
      const isAvailable = await tenantService.checkEmailAvailability(email);
      setEmailAvailable(isAvailable);
      if (!isAvailable) setErrors(prev => ({ ...prev, adminEmail: 'Email already registered' }));
      return isAvailable;
    } catch (error) { return true; } finally { setEmailChecking(false); }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!data.adminName?.trim()) newErrors.adminName = 'Name required';
    if (!data.adminEmail?.trim()) newErrors.adminEmail = 'Email required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.adminEmail)) newErrors.adminEmail = 'Invalid email';
    if (!data.adminPassword?.trim()) newErrors.adminPassword = 'Password required';
    else if (data.adminPassword.length < 8) newErrors.adminPassword = 'Min 8 characters';
    if (data.adminPassword !== data.confirmPassword) newErrors.confirmPassword = 'Passwords mismatch';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (data.adminEmail && emailAvailable === null) {
      const isOk = await checkEmail(data.adminEmail);
      if (!isOk) return;
    }
    if (validateForm()) onNext();
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: COLORS.primary }}>Admin Credentials</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField fullWidth label="Full Name" value={data.adminName || ''} onChange={(e) => onChange('adminName', e.target.value)} error={!!errors.adminName} helperText={errors.adminName} required />
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Email Address" type="email" value={data.adminEmail || ''} onChange={(e) => { onChange('adminEmail', e.target.value); setEmailAvailable(null); }} onBlur={(e) => checkEmail(e.target.value)} error={!!errors.adminEmail} helperText={errors.adminEmail} required />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Password" type="password" value={data.adminPassword || ''} onChange={(e) => onChange('adminPassword', e.target.value)} error={!!errors.adminPassword} helperText={errors.adminPassword} required />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth label="Confirm Password" type="password" value={data.confirmPassword || ''} onChange={(e) => onChange('confirmPassword', e.target.value)} error={!!errors.confirmPassword} helperText={errors.confirmPassword} required />
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button onClick={onBack}>Back</Button>
        <Button variant="contained" onClick={handleNext} disabled={loading || emailChecking} sx={{ px: 6 }}>Next</Button>
      </Box>
    </Box>
  );
};

/**
 * Step 3: Plan Selection (Full Width Cards)
 */
const PlanSelectionStep = ({ data, onChange, onNext, onBack, loading }) => {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    (async () => {
      try {
        const plansData = await subscriptionService.getSubscriptionPlans();
        setPlans(Array.isArray(plansData) ? plansData.filter(p => String(p.status || 'ACTIVE').toUpperCase() === 'ACTIVE') : []);
      } catch (error) { toast.error('Failed to load plans'); } finally { setPlansLoading(false); }
    })();
  }, []);

  const buildPlanFeatures = (plan) => {
    const features = plan.features;
    const hasAnyFlags = features && Object.keys(features).some((k) => PLAN_FEATURE_FLAGS.some((f) => f.id === k));
    const treatAllEnabled = !features || Object.keys(features).length === 0 || !hasAnyFlags;
    return PLAN_FEATURE_FLAGS.map((f) => ({ label: f.label, enabled: treatAllEnabled ? true : !!(features && features[f.id] === true) }));
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h4" align="center" sx={{ fontWeight: 800, mb: 1, color: COLORS.primary }}>Choose Your Plan</Typography>
      <Typography variant="body1" align="center" sx={{ color: COLORS.muted, mb: 4 }}>Scalable pricing for your business needs</Typography>

      <Box display="flex" justifyContent="center" mb={6}>
        <Paper elevation={0} sx={{ p: 0.5, bgcolor: '#e3f2fd', borderRadius: 2, display: 'flex' }}>
          <Button variant={billingCycle === 'monthly' ? 'contained' : 'text'} onClick={() => setBillingCycle('monthly')} sx={{ px: 4, borderRadius: 1.5 }}>Monthly</Button>
          <Button variant={billingCycle === 'yearly' ? 'contained' : 'text'} onClick={() => setBillingCycle('yearly')} sx={{ px: 4, ml: 1, borderRadius: 1.5 }}>Yearly (Save 20%)</Button>
        </Paper>
      </Box>

      {plansLoading ? <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box> : (
        <Grid container spacing={4} justifyContent="center" alignItems="stretch">
          {plans.map((plan) => {
            const isSelected = data.planCode === plan.planCode;
            const price = billingCycle === 'monthly' ? plan.priceMonthly : plan.priceYearly;
            return (
              <Grid item xs={12} md={4} key={plan.id} sx={{ display: 'flex' }}>
                <Card 
                  elevation={isSelected ? 12 : 2}
                  sx={{ 
                    width: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    cursor: 'pointer', 
                    borderColor: isSelected ? COLORS.primary : 'transparent', 
                    borderWidth: 2,
                    borderStyle: 'solid',
                    bgcolor: isSelected ? '#f5f9ff' : '#fff', 
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    transform: isSelected ? 'scale(1.02)' : 'none',
                    borderRadius: 4
                  }} 
                  onClick={() => { onChange('planCode', plan.planCode); onChange('selectedPlan', plan); }}
                >
                  {plan.isPopular && (
                    <Chip label="MOST POPULAR" color="primary" size="small" sx={{ position: 'absolute', top: 16, right: 16, fontWeight: 800 }} />
                  )}
                  <CardContent sx={{ p: 4, flexGrow: 1, textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{plan.name}</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 800, color: COLORS.primary, mb: 2 }}>
                      {formatPrice(price, plan.currency)}
                      <Typography component="span" variant="body1" color="textSecondary">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</Typography>
                    </Typography>
                    <Divider sx={{ my: 3 }} />
                    <Box sx={{ textAlign: 'left' }}>
                      {buildPlanFeatures(plan).map((f, i) => (
                        <Box key={i} display="flex" alignItems="center" mb={1.5}>
                          {f.enabled ? <CheckMarkIcon sx={{ color: COLORS.success, mr: 1.5, fontSize: 20 }} /> : <CloseIcon sx={{ color: COLORS.muted, mr: 1.5, fontSize: 20 }} />}
                          <Typography variant="body2" sx={{ color: f.enabled ? COLORS.text : COLORS.muted }}>{f.label}</Typography>
                        </Box>
                      ))}
                      <Box display="flex" alignItems="center" mt={2}>
                        <CheckMarkIcon sx={{ color: COLORS.success, mr: 1.5, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{plan.trialDays}-Day Free Trial</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                  <Box p={3} pt={0}>
                    <Button fullWidth variant={isSelected ? "contained" : "outlined"} sx={{ py: 1.5, borderRadius: 2 }}>
                      {isSelected ? 'Selected' : 'Select Plan'}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
      <Box display="flex" justifyContent="space-between" mt={6}>
        <Button onClick={onBack} size="large">Back</Button>
        <Button variant="contained" onClick={() => data.planCode && onNext()} disabled={loading || !data.planCode} size="large" sx={{ px: 8 }}>Review & Join</Button>
      </Box>
    </Box>
  );
};

/**
 * Step 4: Complete (Full Width Details)
 */
const CompleteStep = ({ data, onComplete, loading, error }) => (
  <Box sx={{ py: 2 }}>
    <Typography variant="h4" align="center" sx={{ fontWeight: 800, mb: 1, color: COLORS.primary }}>Final Verification</Typography>
    <Typography variant="body1" align="center" sx={{ color: COLORS.muted, mb: 5 }}>Please confirm your details below before creating your account</Typography>

    <Grid container spacing={4} justifyContent="center">
      <Grid item xs={12} md={8}>
        <Card variant="outlined" sx={{ borderRadius: 4, bgcolor: '#f9fafb', border: '1px solid rgba(0,0,0,0.05)' }}>
          <CardContent sx={{ p: 5 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ letterSpacing: 1, mb: 1 }}>ORGANIZATION</Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: COLORS.text }}>{data.companyName}</Typography>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>{data.companyPhone}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {[data.addressLine1, data.addressLine2].filter(Boolean).join(', ')}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>State (GST): {data.stateCode}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary" sx={{ letterSpacing: 1, mb: 1 }}>ADMINISTRATOR</Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: COLORS.text }}>{data.adminName}</Typography>
                <Typography variant="body1" color="textSecondary">{data.adminEmail}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle2" color="textSecondary" sx={{ letterSpacing: 1 }}>SELECTED PLAN</Typography>
                <Typography variant="h4" fontWeight={900} color="primary">{data.selectedPlan?.name}</Typography>
              </Box>
              <Chip 
                label={`${data.selectedPlan?.trialDays || 14} DAYS FREE TRIAL`} 
                sx={{ fontWeight: 800, bgcolor: '#e8f5e9', color: COLORS.success, px: 2, py: 2.5, borderRadius: 2 }} 
              />
            </Box>
          </CardContent>
        </Card>

        <Box mt={4} textAlign="center">
          <FormControlLabel 
            control={<Checkbox checked={data.acceptTerms || false} onChange={(e) => data.onChange('acceptTerms', e.target.checked)} color="primary" />} 
            label={<Typography variant="body1">I agree to the <Link href="/terms" target="_blank">Terms of Service</Link> & <Link href="/privacy" target="_blank">Privacy Policy</Link></Typography>} 
          />
          
          {error && <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>{error}</Alert>}

          <Box mt={5}>
            <Button 
              variant="contained" 
              size="large" 
              onClick={onComplete} 
              disabled={loading || !data.acceptTerms} 
              fullWidth 
              sx={{ py: 2, borderRadius: 3, fontSize: '1.2rem', fontWeight: 800, boxShadow: '0 10px 30px rgba(13, 71, 161, 0.2)' }}
            >
              {loading ? <CircularProgress size={28} color="inherit" /> : 'Create My Account'}
            </Button>
          </Box>
        </Box>
      </Grid>
    </Grid>
  </Box>
);

/**
 * Main Page Component
 */
const TenantRegistrationPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [registrationSettingsLoading, setRegistrationSettingsLoading] = useState(true);
  const [whatsappOtpEnabled, setWhatsappOtpEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await tenantService.getRegistrationOtpSettings();
        if (!cancelled) setWhatsappOtpEnabled(!!s.whatsappOtpEnabled);
      } catch {
        if (!cancelled) setWhatsappOtpEnabled(true);
      } finally {
        if (!cancelled) setRegistrationSettingsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleDataChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleNext = () => setActiveStep(prev => prev + 1);
  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleComplete = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await tenantService.registerTenant({ ...formData, startTrial: true });
      if (response.accessToken) storeUserData(response);
      toast.success('Registration successful!');
      navigate('/dashboard');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Registration failed.';
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const isFullWidthStep = activeStep >= 2;

  if (registrationSettingsLoading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: COLORS.background, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: COLORS.primary }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: COLORS.background, position: 'relative', overflow: 'hidden' }}>
      {/* Background Lottie in Blur View */}
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 0, opacity: 0.1, filter: 'blur(40px)' }}>
        <DotLottieReact src="/lotties/app-login.lottie" autoplay loop style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </Box>

      <Container maxWidth={isFullWidthStep ? "lg" : "lg"} sx={{ position: 'relative', zIndex: 1, py: { xs: 4, md: 8 } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mb: 4, fontWeight: 700, color: COLORS.primary }}>
          Back to Login
        </Button>

        {isFullWidthStep ? (
          <Fade in={true} timeout={600}>
            <Paper elevation={24} sx={{ p: { xs: 3, md: 6 }, borderRadius: 6, bgcolor: COLORS.cardBg, border: '1px solid rgba(255,255,255,0.8)', boxShadow: '0 20px 60px rgba(13, 71, 161, 0.1)' }}>
              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 6 }}>
                {steps.map((step) => (
                  <Step key={step.label}>
                    <StepLabel StepIconProps={{ sx: { fontSize: 28, '&.Mui-active': { color: COLORS.secondary }, '&.Mui-completed': { color: COLORS.success } } }}>
                      <Typography variant="caption" fontWeight={600}>{step.label}</Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
              {activeStep === 2 && <PlanSelectionStep data={{ ...formData, onChange: handleDataChange }} onChange={handleDataChange} onNext={handleNext} onBack={handleBack} loading={loading} />}
              {activeStep === 3 && <CompleteStep data={{ ...formData, onChange: handleDataChange }} onComplete={handleComplete} loading={loading} error={error} />}
            </Paper>
          </Fade>
        ) : (
          <Grid container spacing={8} alignItems="flex-start">
            {/* Left Side: Content & Features */}
            <Grid item xs={12} md={5}>
              <RegistrationSidebar step={activeStep} />
            </Grid>

            {/* Right Side: Registration Form */}
            <Grid item xs={12} md={7}>
              <Paper elevation={24} sx={{ p: { xs: 3, md: 6 }, borderRadius: 4, bgcolor: COLORS.cardBg, border: '1px solid rgba(255,255,255,0.8)', boxShadow: '0 20px 60px rgba(13, 71, 161, 0.12)' }}>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 6 }}>
                  {steps.map((step) => (
                    <Step key={step.label}>
                      <StepLabel StepIconProps={{ sx: { fontSize: 28, '&.Mui-active': { color: COLORS.secondary }, '&.Mui-completed': { color: COLORS.success } } }}>
                        <Typography variant="caption" fontWeight={600}>{step.label}</Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>

                <Fade in={true} key={activeStep} timeout={600}>
                  <Box>
                    {activeStep === 0 && (
                      <CompanyInfoStep
                        data={{ ...formData, onChange: handleDataChange }}
                        onChange={handleDataChange}
                        onNext={handleNext}
                        loading={loading}
                        whatsappOtpEnabled={whatsappOtpEnabled}
                      />
                    )}
                    {activeStep === 1 && <AdminAccountStep data={{ ...formData, onChange: handleDataChange }} onChange={handleDataChange} onNext={handleNext} onBack={handleBack} loading={loading} />}
                  </Box>
                </Fade>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default TenantRegistrationPage;
